import React, { useCallback } from 'react';

import type { CascaderOption } from '../types';
import { isSubmenu } from '../util';

export interface UseCascaderKeyboardInputOptions<
  OptionType extends CascaderOption,
> {
  isOpen: boolean;
  isSearchMode: boolean;
  cursorPath: number[];
  canSelectSubmenu: boolean;
  getOptionsAtLevel: (level: number) => OptionType[];
  onRequestClose: () => void;
  handleSelectOption: (option: OptionType, level: number) => void;
  onHighlightedIndexChange: (level: number, index: number) => void;
  onMoveToParentLevel: (level: number) => void;
  onMoveToChildLevel: () => void;
}

export const useCascaderKeyboardInput = <OptionType extends CascaderOption>({
  isOpen,
  isSearchMode,
  cursorPath,
  canSelectSubmenu,
  getOptionsAtLevel,
  onRequestClose,
  handleSelectOption,
  onHighlightedIndexChange,
  onMoveToParentLevel,
  onMoveToChildLevel,
}: UseCascaderKeyboardInputOptions<OptionType>) => {
  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape' || event.key === 'Tab') {
        onRequestClose();
        return;
      }

      if (!isOpen || isSearchMode) return;

      const activeLevel = cursorPath.length === 0 ? 0 : cursorPath.length - 1;
      const currentOptions = getOptionsAtLevel(activeLevel);
      const currentHighlightedIndex = cursorPath[activeLevel];

      switch (event.key) {
        case 'ArrowUp': {
          event.preventDefault();
          if (
            currentHighlightedIndex !== undefined &&
            currentHighlightedIndex > 0
          ) {
            onHighlightedIndexChange(activeLevel, currentHighlightedIndex - 1);
          }
          return;
        }

        case 'ArrowDown': {
          event.preventDefault();
          if (
            currentHighlightedIndex !== undefined &&
            currentHighlightedIndex < currentOptions.length - 1
          ) {
            onHighlightedIndexChange(activeLevel, currentHighlightedIndex + 1);
          } else if (
            currentHighlightedIndex === undefined &&
            currentOptions.length > 0
          ) {
            onHighlightedIndexChange(activeLevel, 0);
          }
          return;
        }

        case 'ArrowLeft': {
          event.preventDefault();
          onMoveToParentLevel(activeLevel);
          return;
        }

        case 'ArrowRight': {
          event.preventDefault();
          if (
            currentHighlightedIndex !== undefined &&
            currentHighlightedIndex >= 0 &&
            currentHighlightedIndex < currentOptions.length
          ) {
            const highlightedOption = currentOptions[currentHighlightedIndex];
            if (isSubmenu(highlightedOption)) {
              onMoveToChildLevel();
            }
          }
          return;
        }

        case 'Enter': {
          event.preventDefault();
          if (
            currentHighlightedIndex !== undefined &&
            currentHighlightedIndex >= 0 &&
            currentHighlightedIndex < currentOptions.length
          ) {
            const highlightedOption = currentOptions[currentHighlightedIndex];
            if (
              !isSubmenu(highlightedOption) ||
              (canSelectSubmenu && isSubmenu(highlightedOption))
            ) {
              handleSelectOption(highlightedOption, activeLevel);
            }
          }
          return;
        }
      }
    },
    [
      isOpen,
      isSearchMode,
      cursorPath,
      canSelectSubmenu,
      getOptionsAtLevel,
      onRequestClose,
      handleSelectOption,
      onHighlightedIndexChange,
      onMoveToParentLevel,
      onMoveToChildLevel,
    ],
  );

  return { handleInputKeyDown };
};
